import { inject, Injectable, Injector } from '@angular/core';
import { LocalStorageService } from '@web/core';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { blank, Nillable } from '@web/utils';
import { PublicBoxStatusType } from '../enums/public-box-status-type';
import { PublicBoxApiService } from '../api/services/public-box-api.service';
import { PublicBoxAuthorizeRequest } from '../api/requests/public-box-authorize.request';
import { PublicBoxDetailsResponse } from '../api/responses/public-box-details.response';
import { catchNotFoundAndNavigateToNotFoundPage } from '@web/common';

@Injectable({
  providedIn: 'root',
})
export class PublicBoxAuthService {
  private readonly storage = inject(LocalStorageService);
  private readonly publicBoxApi = inject(PublicBoxApiService);

  private readonly injector = inject(Injector);
  private readonly StorageKeyPrefix = '6eb645f9-ffe1-4ba6-bde1-a3115ec7ae06';

  public authorize(slug: string, password: string): Observable<void> {
    return this.publicBoxApi
      .authorize(slug, new PublicBoxAuthorizeRequest(password))
      .pipe(
        map((response) => response.accessToken),
        tap((token) =>
          this.storage.setItem(this.createStorageKey(slug), token),
        ),
        catchNotFoundAndNavigateToNotFoundPage(this.injector),
        blank(),
      );
  }

  public fetchAuthorizedDetails(
    slug: string,
    cacheable = true,
  ): Observable<PublicBoxDetailsResponse> {
    return this.getToken(slug).pipe(
      switchMap((token) =>
        this.publicBoxApi.fetchDetailsBySlug(slug, token, cacheable),
      ),
      catchNotFoundAndNavigateToNotFoundPage(this.injector),
    );
  }

  public isAuthorizationRequired(slug: string): Observable<boolean> {
    return this.publicBoxApi.checkVisibilityBySlug(slug, true).pipe(
      map((response) => response.status),
      map((status) => status === PublicBoxStatusType.Protected),
      catchNotFoundAndNavigateToNotFoundPage(this.injector),
    );
  }

  private getToken(slug: string): Observable<Nillable<string>> {
    const token = this.storage.getItem(this.createStorageKey(slug));

    return of(token);
  }

  private createStorageKey(slug: string): string {
    return `${this.StorageKeyPrefix}:${slug}`;
  }
}
